import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { historyShape, userShape } from 'constants/propTypes';

import { EditButton, PassengerActionItem, PassengerActions } from '../../Passengers.styles';
import config from 'utils/config';
import ReactTooltip from 'react-tooltip';
import { useParams } from 'react-router';
import { getBookingTravelDocumentsURL } from '../../../../../utils/urls';
import SuspendForm from './SuspendForm';
import EditForm from './EditForm';
import AddInfantForm from './AddInfantForm';
import { InsuranceModal } from 'pages/BookingDetails/Journeys/AfterSalesCtas/InsuranceModal/InsuranceModal';

const IS_ADVANCED_PASSENGER_INFORMATION_ENABLED = config('ENABLE_ADVANCED_PASSENGER_INFORMATION', { canOverride: true }) === 'true';

const PassengerFormTable = ({
  passengers,
  availableInfantPassengers,
  canManageInfants = false,
  onAddInfant,
  onDeleteInfant,
  onSave,
  onSuspendRestore,
  isPassengersEditable,
  isBookingConfirmed,
  defaultIsTableInEditMode = false,
  user = {},
  history,
  hasSNTicket,
  intl,
  isVoided = false,
  shouldShowSuspendRestoreCta = false,
  shouldEnableSuspendRestoreCta = false,
  isGDSBooking,
  hasActiveInsurances = false,
  isGroupBooking = false
}) => {
  const [isTableInEditMode, setIsEditable] = useState(defaultIsTableInEditMode);
  const [isTableInSuspendRestoreMode, setIsSuspendRestoreEditable] = useState(false);
  const [isTableInAddInfantMode, setIsAddInfantMode] = useState(false);
  const [warningInsurances, setWarningInsurances] = useState(false);

  const { reference } = useParams();

  const membershipNumber = new URLSearchParams(history?.location?.search || '').get('membershipNumber');

  const isAnyRequired = passengers.some((passenger) => passenger.isTravelDocumentRequired === true);
  const shouldDisableTravelDocumentCapture = !isAnyRequired || !isBookingConfirmed;
  const canAddInfants = canManageInfants;
  const isAddInfantDisabled = availableInfantPassengers.length === 0;

  const toggleIsTableInEditMode = ({ opening = false }) => {
    if (hasActiveInsurances && opening) {
      setWarningInsurances(true);
      return;
    }
    setIsEditable(!isTableInEditMode);
  };

  const toggleIsSuspendRestoreMode = () => {
    setIsSuspendRestoreEditable((state) => !state);
  };

  const toggleIsAddInfantMode = () => {
    setIsAddInfantMode((state) => !state);
  };

  const onCaptureTravelDocument = () => {
    history?.push(getBookingTravelDocumentsURL(reference, membershipNumber));
  };

  const columnSpan = useMemo(() => {
    switch (true) {
      case IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && !isTableInSuspendRestoreMode:
        return 18;
      case isTableInSuspendRestoreMode:
        return 9;
      default:
        return 15;
    }
  }, [isTableInSuspendRestoreMode]);

  return (
    <>
      {!isTableInEditMode && !isTableInSuspendRestoreMode && (
        <PassengerActions data-cy="passengers-edit-controls">
          {warningInsurances && (
            <InsuranceModal
              title={<FormattedMessage id={'exchanges.insuranceWarning.title'} />}
              subtitle={'insurance.warningPaxDetails'}
              onConfirmClick={() => {
                setIsEditable(true);
                setWarningInsurances(false);
              }}
              isOpen={warningInsurances}
              onClose={() => setWarningInsurances(false)}
            />
          )}
          {isPassengersEditable && (
            <PassengerActionItem>
              <div
                data-for="edit-button-tooltip"
                data-tip={
                  isGDSBooking
                    ? intl.formatMessage({ id: 'booking.gds.cannotEdit' })
                    : intl.formatMessage({ id: 'paxForm.edit.tooltipMessage' })
                }
              >
                <EditButton
                  type="button"
                  onClick={() => toggleIsTableInEditMode({ opening: true })}
                  disabled={user.isReadOnly || hasSNTicket || isVoided || isGDSBooking}
                  data-cy="passenger-details-edit"
                >
                  <FormattedMessage id="buttons.edit" />
                </EditButton>
              </div>
              {(hasSNTicket || isGDSBooking) && <ReactTooltip id="edit-button-tooltip" data-cy="edit-button-tooltip" />}
            </PassengerActionItem>
          )}
          {shouldShowSuspendRestoreCta && (
            <PassengerActionItem>
              <EditButton
                type="button"
                onClick={toggleIsSuspendRestoreMode}
                disabled={!shouldEnableSuspendRestoreCta}
                data-cy="passenger-suspend-restore-edit"
                data-testid="passenger-suspend-restore-edit"
              >
                <FormattedMessage id="buttons.suspendRestorePassengers" />
              </EditButton>
            </PassengerActionItem>
          )}
          {canAddInfants && (
            <PassengerActionItem>
              <EditButton
                type="button"
                onClick={toggleIsAddInfantMode}
                disabled={isAddInfantDisabled}
                data-cy="passenger-details-add-infant"
                data-testid="passenger-details-add-infant"
              >
                <FormattedMessage id="buttons.addInfantWithoutSeat" />
              </EditButton>
            </PassengerActionItem>
          )}
          {IS_ADVANCED_PASSENGER_INFORMATION_ENABLED && (
            <>
              <PassengerActionItem data-tip data-for="capture-travel-document" className="advance-pax-info">
                <EditButton
                  type="button"
                  onClick={onCaptureTravelDocument}
                  disabled={shouldDisableTravelDocumentCapture || isVoided}
                  data-cy="passenger-details-travel-documents"
                >
                  <FormattedMessage id="advancedPaxInfo.title" />
                </EditButton>
              </PassengerActionItem>
              <ReactTooltip className="tooltip" id="capture-travel-document" data-cy="travel-document-tooltip" place="bottom">
                <FormattedMessage id="advancedPaxInfo.tooltip" />
              </ReactTooltip>
            </>
          )}
        </PassengerActions>
      )}

      {isTableInSuspendRestoreMode ? (
        <SuspendForm
          passengers={passengers}
          columnSpan={columnSpan}
          isTableInEditMode={isTableInSuspendRestoreMode}
          toggleEditMode={toggleIsSuspendRestoreMode}
          onSave={onSuspendRestore}
        />
      ) : (
        <EditForm
          canManageInfants={canManageInfants}
          isTableInEditMode={isTableInEditMode}
          isPassengersEditable={isPassengersEditable}
          onDeleteInfant={onDeleteInfant}
          toggleEditMode={() => toggleIsTableInEditMode({ opening: false })}
          passengers={passengers}
          columnSpan={columnSpan}
          onSave={onSave}
          isGroupBooking={isGroupBooking}
        />
      )}
      {canManageInfants && (
        <AddInfantForm
          availableInfantPassengers={availableInfantPassengers}
          isOpen={isTableInAddInfantMode}
          onClose={toggleIsAddInfantMode}
          onSave={onAddInfant}
        />
      )}
    </>
  );
};

PassengerFormTable.propTypes = {
  passengers: PropTypes.arrayOf(
    PropTypes.shape({
      passengerId: PropTypes.string.isRequired,
      isDependant: PropTypes.bool,
      isInfant: PropTypes.bool,
      firstName: PropTypes.string.isRequired,
      parentPassengerId: PropTypes.string,
      lastName: PropTypes.string.isRequired,
      birthDate: PropTypes.string,
      email: PropTypes.string,
      phoneNumber: PropTypes.string,
      type: PropTypes.string,
      loyaltyNumber: PropTypes.string,
      isPassengerEditable: PropTypes.bool,
      isTravelDocumentRequired: PropTypes.bool,
      hasTravelDocumentBeenProvided: PropTypes.bool
    }).isRequired
  ).isRequired,
  availableInfantPassengers: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      passengerId: PropTypes.string.isRequired
    }).isRequired
  ).isRequired,
  canManageInfants: PropTypes.bool,
  onAddInfant: PropTypes.func.isRequired,
  onDeleteInfant: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSuspendRestore: PropTypes.func.isRequired,
  isPassengersEditable: PropTypes.bool.isRequired,
  defaultIsTableInEditMode: PropTypes.bool,
  isBookingConfirmed: PropTypes.bool,
  user: userShape,
  history: historyShape,
  hasSNTicket: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  isVoided: PropTypes.bool,
  shouldShowSuspendRestoreCta: PropTypes.bool,
  shouldEnableSuspendRestoreCta: PropTypes.bool,
  isGDSBooking: PropTypes.bool,
  hasActiveInsurances: PropTypes.bool,
  isGroupBooking: PropTypes.bool
};

export default PassengerFormTable;
